import React from 'react';
import { Headphones, MessageCircle, Mail, Phone, Clock, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/user/Navbar';
import Footer from '../../../components/user/Footer';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const Support247: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [benefitsRef, benefitsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 to-white">
        <div className={`max-w-7xl mx-auto transition-all duration-700 ${
          heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <Link 
            to="/services" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Services
          </Link>
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <Headphones className="text-primary-600" size={64} />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-secondary-900 mb-4 tracking-tight">
                24/7 Support
              </h1>
              <p className="text-xl text-secondary-600">
                Round-the-clock customer support to help you with any questions or issues
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-secondary-900 mb-6">
                We're Here When You Need Us
              </h2>
              <p className="text-lg text-secondary-600 leading-relaxed mb-6">
                Our 24/7 Support team is always available to help you with any questions, 
                issues, or concerns. Whether you need technical assistance, account help, 
                or have questions about our services, we're here to support you around the clock.
              </p>
              <p className="text-lg text-secondary-600 leading-relaxed">
                With multiple support channels including live chat, email, phone, and a 
                comprehensive help center, you can reach us in the way that's most convenient 
                for you. Our experienced support team is trained to resolve issues quickly and efficiently.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-12 text-white shadow-soft">
              <Clock size={48} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Always Available</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                Our support team works 24/7 to ensure you get help whenever you need it. 
                No matter what time zone you're in, we're here for you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 px-4 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            featuresVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Support Channels
            </h2>
            <p className="text-xl text-secondary-600">
              Multiple ways to get the help you need
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <MessageCircle size={40} />,
                title: 'Live Chat Support',
                description: 'Get instant help through our live chat system. Connect with support agents in real-time for quick resolutions.'
              },
              {
                icon: <Mail size={40} />,
                title: 'Email Support',
                description: 'Send us an email and get detailed responses within hours. Perfect for complex issues that need thorough explanations.'
              },
              {
                icon: <Phone size={40} />,
                title: 'Phone Support',
                description: 'Speak directly with our support team over the phone. Get immediate assistance for urgent issues.'
              },
              {
                icon: <Headphones size={40} />,
                title: 'Help Center',
                description: 'Comprehensive knowledge base with articles, tutorials, and FAQs. Find answers to common questions instantly.'
              },
              {
                icon: <CheckCircle size={40} />,
                title: 'Ticket System',
                description: 'Submit support tickets for tracking and follow-up. Get updates on your issue status automatically.'
              },
              {
                icon: <Clock size={40} />,
                title: 'Response Time SLA',
                description: 'Guaranteed response times based on your plan. Priority support available for enterprise customers.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  featuresVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            benefitsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Why Choose Our Support?
            </h2>
            <p className="text-xl text-secondary-600">
              Benefits that ensure you're never alone
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: '24/7 Availability',
                description: 'Get help anytime, day or night. Our support team is available around the clock, including weekends and holidays.'
              },
              {
                title: 'Multiple Channels',
                description: 'Choose the support channel that works best for you. Live chat, email, phone, or help center - we\'re accessible everywhere.'
              },
              {
                title: 'Expert Team',
                description: 'Our support team is highly trained and experienced. They understand our platform inside and out.'
              },
              {
                title: 'Fast Response Times',
                description: 'Quick response times ensure you get help when you need it. Priority support available for urgent issues.'
              },
              {
                title: 'Comprehensive Resources',
                description: 'Access to extensive documentation, tutorials, and guides. Learn at your own pace with our help center.'
              },
              {
                title: 'Proactive Support',
                description: 'We don\'t just wait for issues. Our team proactively monitors systems and reaches out when needed.'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 transition-all duration-500 ${
                  benefitsVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <CheckCircle className="text-primary-500 mr-3" size={24} />
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-primary-500">
        <div className={`max-w-4xl mx-auto text-center text-white transition-all duration-700 ${
          ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl font-bold mb-6 tracking-tight">
            Need Help? We're Here!
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Contact our support team anytime - we're available 24/7 to assist you
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-secondary-50 transition-all duration-300 shadow-xl hover:scale-110 hover:-translate-y-1"
            >
              Contact Support
            </Link>
            <Link
              to="/services"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Support247;

